/* eslint-disable react/no-unknown-property */
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@pypestream/design-system';

/**
 * A node-panel textarea that can open into a large editor.
 *
 * The panel is narrow and the long-text fields in it (a Gemini system prompt
 * in a Parameter Input, a description) are capped in height and scroll, so
 * reading or editing a real prompt meant scrolling a 320px box inside a
 * scrolling panel. The expand button opens the SAME value in a dialog sized
 * to the window.
 *
 * The dialog edits through the caller's own handlers, so nothing about how a
 * field commits changes: `onFocus` fires when it opens and `onBlur` when it
 * closes — the same pair the inline textarea fires — so the field's undo
 * snapshot and save happen exactly once per editing session either way.
 */
export const ExpandableTextarea: FC<{
  label: ReactNode;
  /** For the dialog's title and the button's accessible name. */
  labelText: string;
  value: string;
  disabled?: boolean;
  className: string;
  testId: string;
  onFocus?: () => void;
  onChange: (value: string) => void;
  onBlur?: () => void;
  /** Rendered under the inline textarea, e.g. a help line. */
  children?: ReactNode;
}> = ({
  label,
  labelText,
  value,
  disabled,
  className,
  testId,
  onFocus,
  onChange,
  onBlur,
  children,
}) => {
  const [expanded, setExpanded] = useState(false);
  const editorRef = useRef<HTMLTextAreaElement>(null);

  const close = () => {
    setExpanded(false);
    onBlur?.();
  };

  useEffect(() => {
    if (!expanded) return undefined;
    editorRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // The panel and the table both listen for Escape; this one is
        // answered here and goes no further.
        e.stopPropagation();
        close();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
    // `close` only reads props that are stable for an open dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  return (
    <>
      <div className="c-node-property__label-row">
        {label}
        <button
          type="button"
          className="c-node-property__expand"
          test-id={`${testId}-expand`}
          aria-label={`Expand ${labelText}`}
          title="Expand"
          onClick={() => {
            onFocus?.();
            setExpanded(true);
          }}
        >
          <Icon name="expand" size="xsmall" />
        </button>
      </div>
      <textarea
        className={className}
        test-id={testId}
        value={value}
        disabled={disabled}
        onFocus={onFocus}
        onChange={(e) => onChange(e.currentTarget.value)}
        onBlur={onBlur}
      />
      {children}
      {expanded &&
        createPortal(
          <div
            className="c-expanded-text"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) close();
            }}
          >
            <div
              className="c-expanded-text__dialog"
              role="dialog"
              aria-modal="true"
              aria-label={labelText}
              test-id={`${testId}-expanded`}
            >
              <div className="c-expanded-text__head">
                <span className="c-expanded-text__title">{labelText}</span>
                <button
                  type="button"
                  className="c-expanded-text__done"
                  test-id={`${testId}-collapse`}
                  onClick={close}
                >
                  Done
                </button>
              </div>
              <textarea
                ref={editorRef}
                className="c-expanded-text__editor"
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.currentTarget.value)}
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
